import { FlexRow, Typography } from '@metrika/ui-kit';
import { InlineLoading } from '@metrika/ui-kit/carbon';
import { styled, keyframes } from '@metrika/ui-kit/emotion';
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RiskAnalyzeIcon, RiskDiscoverIcon, RiskHomeIcon, RiskMonitorIcon } from '../_risk/_static/icons/icons';
import { renderKriLabels } from '../_risk/kri-catalog-v3/shared';
import { RenderProtocolIconSide } from '../layout-v2/components/SidebarMain/SidebarMainMenu';
import { SearchResultsProps } from './types';

const rollIn = keyframes`
   from {
      opacity: 0;
      transform: translateY(20px) rotateX(-10deg);
   }
   to {
      opacity: 1;
      transform: translateY(0) rotateX(0);
   }
`;

export const ResultsContainer = styled.div`
   display: flex;
   flex-direction: column;
   gap: 0.5rem;
   width: 100%;
   max-width: 800px;
   margin: 0.5rem auto;
   max-height: calc(100vh - 12rem);
   overflow-y: auto;
   perspective: 1000px;
`;

const ResultItem = styled(Link)<{ index: number }>`
   display: flex;
   flex-direction: column;
   gap: 0.5rem;
   text-decoration: none;
   color: ${(props) => props.theme.colors.text01};
   fill: ${(props) => props.theme.colors.text01};
   padding: 1rem;
   border-radius: 0;
   background: ${(props) => props.theme.colors.layer};
   transition: background 0.2s;
   opacity: 0;
   animation: ${rollIn} 0.3s ease-out forwards;
   animation-delay: ${(props) => props.index * 0.05}s;
   transform-origin: top;
   will-change: transform, opacity;

   &:hover {
      background: ${(props) => props.theme.colors.layerHover};
   }
`;

const NoResults = styled.div`
   text-align: center;
   padding: 2rem;
   color: ${(props) => props.theme.colors.text02};
`;

export const SearchResults: React.FC<SearchResultsProps> = ({ results, isLoading, searchTerm, onClose }) => {
   // Generate a unique key when results or search term changes
   const containerKey = useMemo(() => `${searchTerm}-${results?.length ?? 0}`, [searchTerm, results]);
   const location = useLocation();

   if (isLoading) {
      return (
         <ResultsContainer key="loading">
            <InlineLoading description="Loading results..." />
         </ResultsContainer>
      );
   }

   if (searchTerm.length >= 3 && results?.length === 0) {
      return (
         <ResultsContainer key="no-results">
            <NoResults>No results found for "{searchTerm}"</NoResults>
         </ResultsContainer>
      );
   }

   return (
      <ResultsContainer key={containerKey}>
         {results?.length > 0 && (
            <Typography variant={'label01'} style={{ textAlign: 'center', color: 'white' }}>
               {results.length} results
            </Typography>
         )}
         {results.map((result, index) => (
            <ResultItem key={result.id} to={result.link} onClick={onClose} index={index}>
               <FlexRow style={{ gap: '0.5rem', alignItems: 'center', justifyContent: 'flex-start' }}>
                  <div style={{ width: '16px', height: '16px' }}>
                     {result.type === 'dashboard' ? (
                        result.protocol ? (
                           <RenderProtocolIconSide protocol={result.protocol} isSearch />
                        ) : (
                           <RiskAnalyzeIcon />
                        )
                     ) : null}
                     {result.type === 'kri' && <RiskDiscoverIcon />}
                     {result.type === 'assessment' && <RiskMonitorIcon />}
                     {result.type === 'static' && <RiskHomeIcon />}
                  </div>
                  <Typography
                     variant={'productiveHeading01'}
                     color={location.pathname.startsWith(result.link) ? 'link01' : 'text01'}
                  >
                     {result.title}
                  </Typography>
               </FlexRow>
               <Typography variant={'bodyLong01'} color={'text02'}>
                  {result.description}
               </Typography>
               {result.labels && (
                  <FlexRow style={{ gap: '0.5rem', alignItems: 'center', justifyContent: 'flex-start' }}>
                     {renderKriLabels(result.labels)}
                  </FlexRow>
               )}
            </ResultItem>
         ))}
      </ResultsContainer>
   );
};
